import { axios, transformAxiosError } from "../utils/axios.create";
import {
  Term,
  TermCreatePayload,
  TermUpdatePayload,
} from "../domain/terms.interface";

export type TermsFindAllPayload = {
  sortField: string;
  sortOrder: number;
  pageNumber: number;
  pageSize: number;
  search: string;
  active: string;
};

const findAll = async ({
  sortField,
  sortOrder,
  pageNumber,
  pageSize,
  search,
  active,
}: TermsFindAllPayload): Promise<{
  list: Term[];
  totalCount: number;
}> => {
  const queryParams = new URLSearchParams({
    search,
    pageNumber: String(pageNumber + 1),
    pageSize: String(pageSize),
    sortField,
    sortOrder: sortOrder === 1 ? "asc" : "desc",
  });
  if (active) queryParams.append("active", active);

  return axios
    .get(`/terms/search?${queryParams.toString()}`)
    .then((res) => ({
      list: res.data.body.list as Term[],
      totalCount: res.data.body.totalCount as number,
    }))
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

const findActive = async (): Promise<Term | null> => {
  return axios
    .get("/terms/active")
    .then((res) => (res.data.body as Term) || null)
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

const createOne = async (payload: TermCreatePayload): Promise<Term> => {
  return axios
    .post("/terms", payload)
    .then((res) => res.data.body as Term)
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

const updateOne = async ({
  id,
  payload,
}: {
  id: string;
  payload: TermUpdatePayload;
}): Promise<Term> => {
  return axios
    .put(`/terms/${id}`, payload)
    .then((res) => res.data.body as Term)
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

const activateOne = async ({
  id,
  updatedBy,
}: {
  id: string;
  updatedBy: string;
}): Promise<Term> => {
  return axios
    .patch(`/terms/activate/${id}`, { updatedBy })
    .then((res) => res.data.body as Term)
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

const archiveOne = async (id: string): Promise<void> => {
  return axios
    .delete(`/terms/${id}`)
    .then(() => undefined)
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

export const TermsService = {
  findAll,
  findActive,
  createOne,
  updateOne,
  activateOne,
  archiveOne,
};
